import CreateRequestModal from "@/components/pages/requests/components/modals/CreateRequestModal";
import { determineGuestStatus } from "@/components/pages/reservations/guests/utils/guestStatusUtils";
import { Button } from "@/components/ui/button";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { GuestWithLastBooking } from "@/types/guest";
import { RequestType } from "@/types/request";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

interface CreateGuestRequestButtonProps {
  guest: GuestWithLastBooking;
}

export const CreateGuestRequestButton: FC<CreateGuestRequestButtonProps> = ({
  guest,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const activeBooking = guest.lastBooking;
  const guestStatus = determineGuestStatus(activeBooking);

  if (
    !capabilities.canUseRequestsSystem ||
    !activeBooking?.id ||
    !guestStatus
  ) {
    return null;
  }

  const guestDisplayName =
    `${guest.profile.firstName || ""} ${guest.profile.lastName || ""}`.trim() ||
    t("layout.userFallback");
  const roomCategoryName = activeBooking.room?.category?.name;
  const roomNumberLabel = activeBooking.room?.number ?? "—";
  const roomDisplay = roomCategoryName
    ? `${t("requests.form.room")} ${roomNumberLabel} - ${roomCategoryName}`
    : `${t("requests.form.room")} ${roomNumberLabel}`;
  const reservationDisplay = `${guestDisplayName} (${format(
    new Date(activeBooking.arrivalDate),
    "dd.MM.yyyy",
  )} - ${format(new Date(activeBooking.departureDate), "dd.MM.yyyy")})`;

  return (
    <CreateRequestModal
      requestType={RequestType.GUEST}
      bookingId={activeBooking.id}
      guestId={guest.id}
      roomId={activeBooking.roomId}
      initialGuestDisplay={guestDisplayName}
      initialReservationDisplay={reservationDisplay}
      initialRoomDisplay={roomDisplay}
      triggerNode={
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          className="h-8 w-8 p-0"
          title={t("requests.page.addRequest")}
          aria-label={t("requests.page.addRequest")}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Plus className="text-indigo h-4 w-4" />
        </Button>
      }
    />
  );
};
